import React, { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { X, Check, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal di ritaglio interattivo per le immagini prodotto.
 * Usa `react-image-crop` che supporta nativamente:
 * - free-crop con handle ridimensionabili (8 maniglie agli angoli + lati)
 * - aspect ratio fisso (4:3, 1:1, ecc.) con resize proporzionale
 *
 * Le coordinate `pixelCrop` di react-image-crop sono in PIXEL DISPLAY (cioè
 * sull'immagine come renderizzata nella DOM). Per il backend servono coordinate
 * in pixel dell'immagine ORIGINALE → scaliamo per `naturalWidth / displayWidth`.
 */
const PRESETS = [
  { key: "free", label: "Libero", value: undefined },
  { key: "4x3", label: "4:3", value: 4 / 3 },
  { key: "3x2", label: "3:2", value: 3 / 2 },
  { key: "1x1", label: "1:1", value: 1 },
  { key: "16x9", label: "16:9", value: 16 / 9 },
  { key: "3x4", label: "3:4", value: 3 / 4 },
  { key: "2x3", label: "2:3", value: 2 / 3 },
];

// Initial crop centrato su 80% dell'immagine
function defaultCenteredCrop(mediaWidth, mediaHeight, aspect) {
  if (aspect) {
    return centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect, mediaWidth, mediaHeight),
      mediaWidth, mediaHeight,
    );
  }
  return { unit: "%", x: 10, y: 10, width: 80, height: 80 };
}

export default function CropImageModal({ productId, imageId, imageUrl, onClose, onSaved }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [aspect, setAspect] = useState(4 / 3);
  const [saving, setSaving] = useState(false);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    setCrop(defaultCenteredCrop(width, height, aspect));
  }

  function changeAspect(newAspect) {
    setAspect(newAspect);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(defaultCenteredCrop(width, height, newAspect));
    }
  }

  async function saveCrop() {
    if (!completedCrop || !imgRef.current) {
      toast.error("Seleziona prima un'area di ritaglio");
      return;
    }
    const img = imgRef.current;
    // Scaling: dal display alla risoluzione originale
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const payload = {
      x: Math.round(completedCrop.x * scaleX),
      y: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY),
    };
    if (payload.width < 32 || payload.height < 32) {
      toast.error("Area troppo piccola (min 32×32 px)");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/images/${imageId}/crop`, payload);
      toast.success("Ritaglio applicato");
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ritaglio fallito";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  function resetCrop() {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(defaultCenteredCrop(width, height, aspect));
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-ink/85 backdrop-blur-sm flex flex-col" data-testid="crop-modal">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between text-cream border-b border-cream/10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold">Ritaglia immagine</div>
          <div className="font-display text-lg">
            {aspect ? "Trascina gli angoli per ridimensionare" : "Modalità libera — trascina ovunque + ridimensiona"}
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center text-cream/70 hover:text-cream hover:bg-cream/10 rounded-sm"
          aria-label="Chiudi"
          data-testid="crop-close"
        ><X size={20} /></button>
      </div>

      {/* Cropper area */}
      <div className="flex-1 relative min-h-0 bg-black flex items-center justify-center overflow-auto p-3">
        <ReactCrop
          crop={crop}
          onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspect}
          keepSelection
          minWidth={32}
          minHeight={32}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Source"
            onLoad={onImageLoad}
            crossOrigin="anonymous"
            style={{ maxHeight: "calc(100vh - 280px)", maxWidth: "100%", display: "block" }}
            data-testid="crop-image"
          />
        </ReactCrop>
      </div>

      {/* Controls */}
      <div className="bg-cream px-4 md:px-5 py-3 border-t border-ink/10 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        {/* Aspect presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-[0.2em] uppercase text-slate1">Proporzioni</span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => changeAspect(p.value)}
              className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-sm border transition-colors ${
                aspect === p.value
                  ? "bg-ink text-cream border-ink"
                  : "bg-white text-ink border-ink/20 hover:border-ink/50"
              }`}
              data-testid={`crop-aspect-${p.key}`}
            >{p.label}</button>
          ))}
          <button
            onClick={resetCrop}
            className="px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-sm border border-ink/20 hover:border-ink/50 inline-flex items-center gap-1 ml-auto"
            title="Reset selezione"
            data-testid="crop-reset"
          >
            <RefreshCw size={12} /> Reset
          </button>
        </div>

        {/* Live preview info */}
        {completedCrop && imgRef.current && (
          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 tabular-nums">
            Selezione: <span className="text-ink">{Math.round(completedCrop.width * (imgRef.current.naturalWidth / imgRef.current.width))} × {Math.round(completedCrop.height * (imgRef.current.naturalHeight / imgRef.current.height))} px</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink"
            data-testid="crop-cancel"
          >Annulla</button>
          <button
            onClick={saveCrop}
            disabled={saving || !completedCrop}
            className="px-6 py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="crop-save"
          >
            <Check size={14} />
            {saving ? "Salvataggio…" : "Applica ritaglio"}
          </button>
        </div>
      </div>
    </div>
  );
}
